import { createEntityAdapter, EntityAdapter, EntityState, Dictionary, Update } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on, select } from '@ngrx/store';
import * as Actions from './actions'
import * as fromRouter from '../router/router-state.selectors';
import { INotification, INotificationVM } from 'app/models/notification';
import { notificationFilters, notificationConsts } from 'app/services/constants';
import * as fromKeg from 'app/state/keg/reducer';
import { environment } from 'environments/environment';
import { concat } from 'rxjs';
import { deleteAllTapNotificationsSuccess } from './actions';

export interface State extends EntityState<INotification> {
  // additional entity state properties
  selectedId: string | null;
  navigationRoute: string,
  updating: boolean;
  unreadCount: number,
  readCount: number,
  unreadTapCount: number,
  unreadBeverageCount: number,
  readTapCount: number,
  totalTapCount: number,
  notificationFilter: string[],
  error: string
}

export function sortByUDate(a: INotification, b: INotification): number {
  return getTime(b.uDate) - getTime(a.uDate);
}

function getTime(date?: Date) {
  return date != null ? date.getTime() : 0;
}



export function selectId(a: INotification): string {
  return a.id ?? '';
}
export const adapter: EntityAdapter<INotification> = createEntityAdapter<INotification>({
  sortComparer: sortByUDate,
  selectId: selectId
});

export const initialState: State = adapter.getInitialState({
  selectedId: null,
  navigationRoute: '',
  updating: false,
  unreadCount: 0,
  readCount: 0,
  readTapCount: 0,
  unreadTapCount: 0,
  totalTapCount: 0,
  unreadBeverageCount: 0,
  notificationFilter: [],
  error: '',
});

export const stateName = 'notification';

export const reducer = createReducer<State>(
  initialState,
  on(Actions.clearAllItems, (state) => {

    return adapter.removeAll({
      ...state,
      selectedId: null,
      readCount: 0,
      unreadCount: 0,
      totalTapCount: 0,
      readTapCount: 0,
      unreadTapCount: 0,
      unreadBeverageCount: 0
    });
  }),
  on(Actions.loadItemsSuccess, (state, { items }) => {
    return adapter.setMany(items, state);
  }),
  on(Actions.loadUnreadItemsSuccess, (state, { items }) => {
    return adapter.setMany(items, state);
  }),
  on(Actions.loadItemsAfterSuccess, (state, { items }) => {
    return adapter.setMany(items, state);
  }),
  on(
    Actions.updateItem,
    (state, { item }) => {
      return adapter.setOne(item, state);
    }),
  // on(
  //   Actions.softUpdateSuccess,
  //   (state, { data }) => {
  //     return adapter.setOne(data, state);
  //   }),
  on(Actions.deleteItem, (state, action) => {
    return adapter.removeOne(action.id, state);
  }),

  on(Actions.removeAllReadNotifications, (state, action) => {
    const ids = Object.keys(state.entities).filter(id => state.entities[id]?.read);
    return adapter.removeMany(ids, state);
  }),
  on(Actions.removeAllNotifications, (state, action) => {
    return adapter.removeAll(state);
  }),
  on(Actions.setItem, (state, action) => Object.assign({ ...state, selectedId: action.id })),

  on(Actions.setItemFromListPage,
    (state, action) => {
      return Object.assign({
        ...state, selectedId: action.id
      })
    }),

  on(Actions.loadCountReadNotificationsSuccess,
    (state, action) => {
      return Object.assign({
        ...state, readCount: action.count
      })
    }),

  on(Actions.loadCountUnreadNotificationsSuccess,
    (state, action) => {
      return Object.assign({
        ...state, unreadCount: action.count
      })
    }),

  on(Actions.loadCountAllTapNotificationsSuccess,
    (state, action) => {
      return Object.assign({
        ...state, totalTapCount: action.count
      })
    }),

  on(Actions.loadCountReadTapNotificationsSuccess,
    (state, action) => {
      return Object.assign({
        ...state, readTapCount: action.count
      })
    }),

  on(Actions.loadCountUnreadTapNotificationsSuccess,
    (state, action) => {
      return Object.assign({
        ...state, unreadTapCount: action.count
      })
    }),


  on(Actions.setNotificationRoute,
    (state, action) => {
      return Object.assign({
        ...state, navigationRoute: action.route
      })
    }),

  on(Actions.softUpdateSuccess,
    (state, action) => {
      const item = state.entities[action.id];
      if (item) {
        if (item.read !== action.data.read) {
          const data = { ...action.data, id: action.id };
          return adapter.updateOne(data, state);
        } else {
          return { ...state };
        }
      } else {
        return { ...state };
      }
    }),

  on(Actions.setNotificationFilter,
    (state, action) => {
      const exists = state.notificationFilter.includes(action.filter);
      const notificationFilter = exists
        ? state.notificationFilter.filter(f => f !== action.filter)
        : [...state.notificationFilter, action.filter];
      return { ...state, notificationFilter };
    }),



  on(Actions.setRead,
    (state, action) => {
      return Object.assign({
        ...state, updating: true
      })
    }),

  on(Actions.setReadSuccess,
    (state, action) => {
      const item = state.entities[action.id];
      if (item) {
        if (!item.read) {
          const updatedState = Object.assign({ ...state, updating: false });
          const data: Update<INotification> = { id: action.id, changes: { read: true } };
          return adapter.updateOne(data, updatedState);
        } else {
          const updatedState = Object.assign({ ...state, updating: false });
          return { ...updatedState };
        }
      } else {
        const updatedState = Object.assign({ ...state, updating: false });
        return { ...updatedState };
      }
    }),

  on(
    Actions.markAllAsRead,
    Actions.deleteAllReadNotifications,
    Actions.deleteAllNotifications,
    Actions.markAllTapAsRead,
    Actions.deleteAllReadTapNotifications,
    Actions.deleteAllTapNotifications,
    (state, action) => {
      return Object.assign({
        ...state, updating: true
      })
    }),

  on(Actions.markAllAsReadSuccess, (state) => {
    // Mark all locally-known unread items as read so the unread-list view
    // (filter `!read`) clears immediately — the live `where('read', '==', false)`
    // subscription emits without these items, but `setMany` won't remove the
    // existing stale entries.
    const unreadIds = (state.ids as string[]).filter(id => state.entities[id]?.read === false);
    const updates = unreadIds.map(id => ({ id, changes: { read: true } }));
    return adapter.updateMany(updates, { ...state, updating: false });
  }),

  on(Actions.markAllTapAsReadSuccess, (state) => {
    const tapUnreadIds = (state.ids as string[]).filter(id => {
      const e = state.entities[id];
      return e && e.read === false && e.type === notificationConsts.type_tap;
    });
    const updates = tapUnreadIds.map(id => ({ id, changes: { read: true } }));
    return adapter.updateMany(updates, { ...state, updating: false });
  }),

  on(Actions.deleteAllNotificationsSuccess, (state) => {
    return adapter.removeAll({ ...state, updating: false });
  }),

  on(Actions.deleteAllReadNotificationsSuccess, (state) => {
    const readIds = (state.ids as string[]).filter(id => state.entities[id]?.read === true);
    return adapter.removeMany(readIds, { ...state, updating: false });
  }),

  on(Actions.deleteAllTapNotificationsSuccess, (state) => {
    const tapIds = (state.ids as string[]).filter(id => state.entities[id]?.type === notificationConsts.type_tap);
    return adapter.removeMany(tapIds, { ...state, updating: false });
  }),

  on(Actions.deleteAllReadTapNotificationsSuccess, (state) => {
    const readTapIds = (state.ids as string[]).filter(id => {
      const e = state.entities[id];
      return e && e.read === true && e.type === notificationConsts.type_tap;
    });
    return adapter.removeMany(readTapIds, { ...state, updating: false });
  }),
  on(
    Actions.markAllAsReadFailure,
    Actions.markAllTapAsReadFailure,
    (state) => {
      return { ...state, updating: false };
    }),
  on(
    Actions.deleteAllReadNotificationsFailure,
    Actions.deleteAllNotificationsFailure,
    Actions.markAllTapAsReadFailure,
    Actions.deleteAllReadTapNotificationsFailure,
    Actions.deleteAllTapNotificationsFailure,
    (state, action) => {
      return Object.assign({ ...state, updating: false });
    }),

);

// get the selectors
const {
  selectAll,
  selectEntities: selectItemEntities,
} = adapter.getSelectors();

export const getSelectedId = (state: State) => state.selectedId;
export const getUpdating = (state: State) => state.updating;
export const getReadCount = (state: State) => state.readCount;
export const getUnreadCount = (state: State) => state.unreadCount;
export const getTotalTapCount = (state: State) => state.totalTapCount;
export const getReadTapCount = (state: State) => state.readTapCount;
export const getUnreadTapCount = (state: State) => state.unreadTapCount;
export const getNotificationFilter = (state: State) => state.notificationFilter;

const getState = createFeatureSelector<State>(stateName);
export const selectNotificationFilter = createSelector(getState, getNotificationFilter);
export const selectAllItems = createSelector(getState, selectAll);
export const selectAllItemsWithNotificationFilter = createSelector(
  selectAllItems, selectNotificationFilter, (items, filter) => {
    if (filter.length === 0) return items;
    return items.filter(x => filter.includes(x.type));
  });


export const selectAllEntities = createSelector(getState, selectItemEntities);

export const selectUpdating = createSelector(getState, getUpdating);
export const selectReadCount = createSelector(getState, getReadCount);
export const selectUnreadCount = createSelector(getState, getUnreadCount);
export const selectReadTapCount = createSelector(getState, getReadTapCount);
export const selectUnreadTapCount = createSelector(getState, getUnreadTapCount);
export const selectTotalCount = createSelector(selectReadCount, selectUnreadCount, (read, unread) => read + unread);
export const selectTotalTapCount = createSelector(selectReadTapCount, selectUnreadTapCount, (read, unread) => read + unread);

export const selectCurrentId = createSelector(getState, getSelectedId);
export const selectCurrentItem = createSelector(selectAllEntities, selectCurrentId, (entities, id) => {
  if (id) return entities[id];
  return null;
});

export const selectEntity = (id: string) => createSelector(selectAllEntities, entities => entities[id]);
export const selectNotificationRoute = createSelector(getState, state => state.navigationRoute);

export const selectUnReadItems = createSelector(
  selectAllItems,
  (items) => items.filter(x => !x.read)
);

export const selectUnReadItemsWithNotificationFilter = createSelector(
  selectAllItemsWithNotificationFilter,
  (items) => {
    return items.filter(x => !x.read);
  }
);

export const selectReadItems = createSelector(
  selectAllItems,
  (items) => items.filter(x => x.read)
);

export const selectReadItemsWithNotificationFilter = createSelector(
  selectAllItemsWithNotificationFilter,
  (items) => items.filter(x => x.read)
);

export const getFromParams = createSelector(
  fromRouter.selectRouteParams,
  selectAllEntities,
  ({ id }, items) => {
    return items[id];
  }
);

export const getWithContentFromParams = createSelector(
  fromRouter.selectRouteParams,
  fromKeg.getKegsWithUnitBeverageAndLocation,
  selectAllEntities,
  ({ id }, kegs, items) => {
    const item = items[id];
    let retItem = { ...item } as INotificationVM;
    if (!item?.externalId) return retItem;
    const keg = kegs.find(x => x.id === item.externalId);
    retItem.keg = keg;

    // switch (item.type) {
    //   case notificationConsts.type_tap:
    //     const keg = kegs.find(x => x.id === item.externalId);
    //     retItem.keg = keg;
    //     break;
    //   case notificationConsts.alert:
    //     const keg2 = kegs.find(x => x.id === item.externalId);
    //     retItem.keg = keg2;
    //     break;

    // }

    return retItem;
  }
);
export const selectIsNotificationFilterBeverage = createSelector(
  selectNotificationFilter, filter => filter.includes(notificationFilters.beverage));
export const selectIsNotificationFilterTap = createSelector(
  selectNotificationFilter, filter => filter.includes(notificationFilters.tap));
export const selectIsNotificationFilterSupport = createSelector(
  selectNotificationFilter, filter => filter.includes(notificationFilters.support));
